import { useToast } from '@/hooks/use-toast'
import { deathDataApi } from '@/rest/modules/deathData-api'
import { DeathData } from '@/domain/deathData/deathData'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const deathDataKeys = {
  all: ['deathData'] as const,
  lists: () => [...deathDataKeys.all, 'list'] as const,
  details: () => [...deathDataKeys.all, 'detail'] as const,
  detail: (id: string) => [...deathDataKeys.details(), id] as const,
  byContract: (contractId: string) => [...deathDataKeys.all, 'contract', contractId] as const,
}

export const useDeathDataByContract = (contractId: string) => {
  return useQuery({
    queryKey: deathDataKeys.byContract(contractId),
    queryFn: () => deathDataApi.getDeathDataByContract(contractId),
    enabled: !!contractId,
    staleTime: 0,
  })
}

export const useDeathData = (id: string) => {
  return useQuery({
    queryKey: deathDataKeys.detail(id),
    queryFn: () => deathDataApi.getDeathData(id),
    enabled: !!id,
  })
}

export const useCreateDeathData = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: DeathData) => deathDataApi.createDeathData(data),
    onSuccess: () => {
      toast({
        title: 'Éxito',
        description: 'Datos de fallecimiento creados correctamente',
      })
      queryClient.invalidateQueries({ queryKey: deathDataKeys.all })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudieron crear los datos de fallecimiento',
        variant: 'destructive',
      })
      console.error('Error creating death data:', error)
    },
  })
}

export const useUpdateDeathData = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DeathData> }) =>
      deathDataApi.updateDeathData(id, data),
    onSuccess: () => {
      toast({
        title: 'Éxito',
        description: 'Datos de fallecimiento actualizados correctamente',
      })
      queryClient.invalidateQueries({ queryKey: deathDataKeys.all })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudieron actualizar los datos de fallecimiento',
        variant: 'destructive',
      })
      console.error('Error updating death data:', error)
    },
  })
}

export const useDeleteDeathData = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => deathDataApi.deleteDeathData(id),
    onSuccess: () => {
      toast({
        title: 'Éxito',
        description: 'Datos de fallecimiento eliminados correctamente',
      })
      queryClient.invalidateQueries({ queryKey: deathDataKeys.all })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudieron eliminar los datos de fallecimiento',
        variant: 'destructive',
      })
      console.error('Error deleting death data:', error)
    },
  })
}
